import React, { useState, useEffect, useCallback } from 'react';
import { Icons } from '../constants';

import { useUIStore } from '../stores/useUIStore';

// Check if running in Tauri
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

// Lazy load Tauri API only when needed
let tauriInvoke: ((cmd: string) => Promise<unknown>) | null = null;

if (isTauri) {
    import('@tauri-apps/api/core').then(({ invoke }) => {
        tauriInvoke = invoke;
    });
}

const SaveStatusIndicator = () => {
    const { saveStatus: status, actions: { triggerRetrySave } } = useUIStore();

    if (status === 'idle') return null;

    return (
        <div className="flex items-center gap-1.5 ml-3 px-2 py-0.5 rounded-full bg-ui-surface/50 border border-white/5 animate-in fade-in duration-300">
            {status === 'saving' && (
                <>
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    <span className="text-[9px] font-medium text-text-muted uppercase tracking-wider">Saving</span>
                </>
            )}
            {status === 'saved' && (
                <>
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-[9px] font-medium text-text-muted uppercase tracking-wider">Saved</span>
                </>
            )}
            {status === 'error' && (
                <button
                    onClick={triggerRetrySave}
                    className="flex items-center gap-1.5 group hover:bg-white/5 -my-0.5 -mx-1 px-1 py-0.5 rounded-full transition-colors"
                >
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 group-hover:bg-red-400" />
                    <span className="text-[9px] font-medium text-red-500 group-hover:text-red-400 uppercase tracking-wider">Error · Retry</span>
                    <Icons.RefreshCcw className="w-3 h-3 text-red-500 group-hover:text-red-400" />
                </button>
            )}
        </div>
    );
};

export const TitleBar = ({ appName = "NOTESPACE" }: { appName?: string }) => {
    const [isMaximized, setIsMaximized] = useState(false);
    const [appVersion, setAppVersion] = useState('');

    useEffect(() => {
        if (!isTauri) return;

        // Get app version
        const getVersion = async () => {
            if (tauriInvoke) {
                try {
                    const version = await tauriInvoke('get_app_version') as string;
                    setAppVersion(version);
                } catch (e) {
                    console.error('Failed to get app version:', e);
                }
            }
        };

        // Small delay to ensure Tauri API is loaded
        const timer = setTimeout(getVersion, 100);

        return () => clearTimeout(timer);
    }, []);

    const handleMinimize = useCallback(async () => {
        if (tauriInvoke) {
            await tauriInvoke('minimize_window');
        }
    }, []);

    const handleMaximize = useCallback(async () => {
        if (tauriInvoke) {
            await tauriInvoke('maximize_window');
            setIsMaximized(prev => !prev);
        }
    }, []);

    const handleClose = useCallback(async () => {
        if (tauriInvoke) {
            await tauriInvoke('close_window');
        }
    }, []);

    if (!isTauri) return null;

    return (
        <div className="h-9 bg-ui-main/80 backdrop-blur-xl border-b border-ui-border flex items-center justify-between select-none z-[9999] relative shadow-sm" data-tauri-drag-region>
            {/* Left: Logo & App Name */}
            <div className="flex items-center gap-2.5 pl-3">
                {/* App Icon */}
                <img
                    src="/notescape.png"
                    alt="NoteSpace"
                    className="w-6 h-6 rounded-md"
                />
                <div className="flex items-baseline gap-2">
                    <span className="font-black text-[11px] tracking-[0.15em] text-text-main">{appName}</span>
                    {appVersion && (
                        <span className="text-[9px] font-bold text-text-muted tracking-wide">v{appVersion}</span>
                    )}
                </div>

                {/* Save Status Indicator */}
                <SaveStatusIndicator />
            </div>

            {/* Center: Drag Area (Empty for now, could add tabs later) */}
            <div className="flex-1" data-tauri-drag-region />

            {/* Right: Window Controls (Windows 11 Style) */}
            <div className="flex items-center h-full">
                {/* Minimize */}
                <button
                    onClick={handleMinimize}
                    className="group w-12 h-full flex items-center justify-center hover:bg-ui-surface transition-colors duration-150"
                    title="Minimize"
                >
                    <div className="w-3 h-[1.5px] bg-text-muted group-hover:bg-text-main transition-colors" />
                </button>

                {/* Maximize/Restore */}
                <button
                    onClick={handleMaximize}
                    className="group w-12 h-full flex items-center justify-center hover:bg-ui-surface transition-colors duration-150"
                    title={isMaximized ? "Restore" : "Maximize"}
                >
                    {isMaximized ? (
                        // Restore icon (overlapping squares)
                        <div className="relative w-[10px] h-[10px]">
                            <div className="absolute top-0 right-0 w-[8px] h-[8px] border-[1.5px] border-text-muted group-hover:border-text-main" />
                            <div className="absolute bottom-0 left-0 w-[8px] h-[8px] border-[1.5px] border-text-muted group-hover:border-text-main bg-ui-main" />
                        </div>
                    ) : (
                        // Maximize icon (single square)
                        <div className="w-[10px] h-[10px] border-[1.5px] border-text-muted group-hover:border-text-main transition-colors" />
                    )}
                </button>

                {/* Close */}
                <button
                    onClick={handleClose}
                    className="group w-12 h-full flex items-center justify-center hover:bg-red-500 transition-colors duration-150"
                    title="Close"
                >
                    <Icons.X className="w-4 h-4 text-text-muted group-hover:text-white transition-colors" />
                </button>
            </div>
        </div>
    );
};
